import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { buttonVariants } from "@/components/ui/button";
import { Checkbox, Textarea } from "@/components/ui/form-controls";
import { Select } from "@/components/ui/select";
import { WIP_ACTION_HINT, WipHint } from "@/components/ui/wip-hint";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { cn } from "@/lib/utils";

/**
 * `/approvals/:id/escalate` — Reject / Hold / Escalate.
 *
 * Layout ported from `.design-ref/prototype/app/(enterprise-wallet)/approvals/[id]/escalate/page-client.tsx`
 * (the `ApprovalEscalate` feature component). This route used to resolve a
 * MOCK_APPROVAL_DETAILS fixture and apply reject/hold/escalate to a local,
 * browser-only store, and it was then deferred wholesale (ALL-5707): nothing
 * reached the backend and the approvers on screen were invented.
 *
 * This is the deferred page's layout, without the missing data. The designer's
 * structure stays — breadcrumb, title, the three decision tabs, and the
 * dangerous-action panel with its reason/notes fields — but every control whose
 * value or effect comes from the missing backend is disabled and says so, and
 * every value the prototype hardcoded renders the shared "not available yet"
 * marker. There is no toast, no local store and no fabricated approval: the
 * reason lists (policy data), the transfer reference and the decision itself
 * all come from the approval backend, which does not exist in this environment.
 * Rejecting a real approval already lives on `/approvals/:id` via the gateway;
 * hold and escalate have no backend route or permission.
 */
type EscalateTab = "reject" | "hold" | "escalate";

const TABS: { id: EscalateTab; label: string }[] = [
  { id: "reject", label: "Reject" },
  { id: "hold", label: "Hold for Review" },
  { id: "escalate", label: "Escalate" },
];

const TAB_HEADING: Record<EscalateTab, string> = {
  reject: "Reject Transfer",
  hold: "Hold Transfer",
  escalate: "Escalate Transfer",
};

const TAB_CONSEQUENCE: Record<EscalateTab, string> = {
  reject:
    "When this is available, rejecting a transfer terminates it, notifies the requester and generates an evidence receipt. Nothing happens here yet.",
  hold:
    "When this is available, holding a transfer pauses it until a decision is made, notifies the requester and records the action in the audit trail. Nothing happens here yet.",
  escalate:
    "When this is available, escalating a transfer reassigns it to a senior approver for review and notifies the requester and the approval route. Nothing happens here yet.",
};

const TAB_CONFIRM: Record<EscalateTab, string> = {
  reject: "Confirm Rejection",
  hold: "Confirm Hold",
  escalate: "Confirm Escalation",
};

export default function ApprovalEscalatePage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<EscalateTab>("reject");
  const [notes, setNotes] = useState("");
  const [holdReason, setHoldReason] = useState("");
  const [evidence, setEvidence] = useState(true);

  const backHref = id ? `/approvals/${id}` : "/approvals";
  // One stable id so every disabled control points at the same visible reason.
  const hintId = "approval-escalate-wip-hint";

  return (
    <>
      <Breadcrumb items={[{ label: "Approvals", href: "/approvals" }, { label: "Reject / Hold / Escalate" }]} />

      <div className="max-w-[680px]">
        <div className="flex flex-wrap items-center gap-x-2">
          <h1 className="text-xl font-bold text-ink">Reject / Hold / Escalate</h1>
          <WipHint />
        </div>
        <p className="mt-1 text-[12.5px] text-muted">
          Approval {id ? <span className="font-mono">{id}</span> : <UnavailableValue />} — Transfer{" "}
          <UnavailableValue />
        </p>
        <p role="status" className="mt-1 text-[12.5px] text-muted">
          Hold and escalate are not available in this environment yet
        </p>

        <div aria-label="Decision type" className="mt-5 flex flex-wrap gap-2" role="group">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              aria-pressed={tab === t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                "rounded-md border px-5 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
                tab === t.id
                  ? "border-danger bg-danger text-white"
                  : "border-line bg-surface text-ink hover:bg-bg"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-5 rounded-lg border border-danger-border bg-danger-soft p-6">
          <div className="text-[17px] font-bold text-danger">
            {TAB_HEADING[tab]} <UnavailableValue />
            {tab === "hold" && " for Review"}
          </div>
          <p className="mt-1 max-w-[552px] text-[12.5px] leading-relaxed text-warning">{TAB_CONSEQUENCE[tab]}</p>

          <fieldset disabled aria-describedby={hintId} className="contents">
            {tab === "reject" && (
              <>
                <div className="mt-4">
                  <label htmlFor="reject-reason" className="mb-1.5 block text-[12px] font-semibold text-ink">
                    Rejection Reason <span className="text-danger">(required)</span>
                  </label>
                  <Select
                    id="reject-reason"
                    disabled
                    title={WIP_ACTION_HINT}
                    className="w-full border-danger/50 focus:border-danger"
                  >
                    <option value="">Reason list not available yet</option>
                  </Select>
                </div>
                <div className="mt-3">
                  <label htmlFor="reject-notes" className="mb-1.5 block text-[12px] font-semibold text-ink">
                    Additional Notes
                  </label>
                  <Textarea
                    id="reject-notes"
                    rows={3}
                    disabled
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    title={WIP_ACTION_HINT}
                    className="border-danger/50 focus:border-danger"
                    placeholder="Optional context for the audit record"
                  />
                </div>
              </>
            )}

            {tab === "hold" && (
              <div className="mt-4">
                <label htmlFor="hold-notes" className="mb-1.5 block text-[12px] font-semibold text-ink">
                  Hold Reason <span className="text-danger">(required)</span>
                </label>
                <Textarea
                  id="hold-notes"
                  rows={3}
                  disabled
                  value={holdReason}
                  onChange={(e) => setHoldReason(e.target.value)}
                  title={WIP_ACTION_HINT}
                  className="border-danger/50 focus:border-danger"
                  placeholder="Describe why this transfer is being held for review"
                />
              </div>
            )}

            {tab === "escalate" && (
              <>
                <div className="mt-4">
                  <label htmlFor="escalate-reason" className="mb-1.5 block text-[12px] font-semibold text-ink">
                    Escalation Reason <span className="text-danger">(required)</span>
                  </label>
                  <Select
                    id="escalate-reason"
                    disabled
                    title={WIP_ACTION_HINT}
                    className="w-full border-danger/50 focus:border-danger"
                  >
                    <option value="">Reason list not available yet</option>
                  </Select>
                </div>
                <div className="mt-3">
                  <label htmlFor="escalate-notes" className="mb-1.5 block text-[12px] font-semibold text-ink">
                    Additional Context
                  </label>
                  <Textarea
                    id="escalate-notes"
                    rows={3}
                    disabled
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    title={WIP_ACTION_HINT}
                    className="border-danger/50 focus:border-danger"
                    placeholder="Optional supporting notes for the senior approver"
                  />
                </div>
              </>
            )}

            {/*
              The shared `Checkbox` primitive takes no prop passthrough, so the
              reason hangs off the row (title) and the `fieldset` above carries the
              `aria-describedby` association for the whole disabled group.
            */}
            <div className="mt-3 flex items-center gap-2" title={WIP_ACTION_HINT}>
              <Checkbox
                checked={evidence}
                onCheckedChange={setEvidence}
                disabled
                ariaLabel="Generate evidence packet for audit"
              />
              <span className="text-[13px] text-ink">Generate evidence packet for audit</span>
            </div>

            <div className="mt-4 flex items-center gap-2.5">
              <button
                type="button"
                disabled
                title={WIP_ACTION_HINT}
                aria-describedby={hintId}
                className={cn(buttonVariants({ variant: "destructive" }), "disabled:opacity-40")}
              >
                {TAB_CONFIRM[tab]}
              </button>
              <Link to={backHref} className={buttonVariants({ variant: "secondary" })}>
                Cancel
              </Link>
            </div>
          </fieldset>

          <p id={hintId} className="mt-3 text-xs text-muted">
            {WIP_ACTION_HINT}
          </p>
        </div>
      </div>
    </>
  );
}
